<template>
  <div
    class="canvas-zoom-controls"
    role="toolbar"
    :aria-label="$t('editor.canvasViewControls')"
    @pointerdown.stop
    @mousedown.stop
    @touchstart.stop
    @click.stop
  >
    <span class="zoom-label">{{ $t('editor.zoom') }}</span>
    <div class="zoom-stepper">
      <button
        type="button"
        class="zoom-button"
        :aria-label="$t('editor.zoomOut')"
        :title="$t('editor.zoomOut')"
        :disabled="zoom <= 1"
        @click="$emit('zoom-out')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 12h14" />
        </svg>
      </button>
      <output class="zoom-level" :aria-label="$t('editor.zoomLevel', { percent: zoomPercent })" aria-live="polite">
        {{ zoomPercent }}%
      </output>
      <button
        type="button"
        class="zoom-button"
        :aria-label="$t('editor.zoomIn')"
        :title="$t('editor.zoomIn')"
        :disabled="zoom >= 5"
        @click="$emit('zoom-in')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
    </div>
    <button
      type="button"
      class="zoom-button fit-button"
      :aria-label="$t('editor.fitCanvas')"
      :title="$t('editor.fitCanvas')"
      @click="$emit('fit')"
    >
      {{ $t('editor.fitCanvasShort') }}
    </button>
    <button
      type="button"
      class="zoom-button pan-button"
      :class="{ 'is-active': panMode }"
      :aria-label="$t(panMode ? 'editor.panModeOn' : 'editor.panMode')"
      :title="$t(panMode ? 'editor.panModeOn' : 'editor.panMode')"
      :aria-pressed="panMode"
      @click="$emit('toggle-pan')"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8 11V5.5a1.5 1.5 0 0 1 3 0V10 4.5a1.5 1.5 0 0 1 3 0V10 5.5a1.5 1.5 0 0 1 3 0V12l.6-.7a1.7 1.7 0 0 1 2.8 1.7l-1.1 4.2A5 5 0 0 1 16.5 21h-4a5 5 0 0 1-3.6-1.5L5.4 16a1.8 1.8 0 0 1 2.5-2.6L10 15" />
      </svg>
      <span class="pan-label">{{ $t('editor.pan') }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'CanvasZoomControls',
  props: {
    zoom: { type: Number, default: 1 },
    panMode: { type: Boolean, default: false },
    isMobile: { type: Boolean, default: false }
  },
  emits: ['zoom-in', 'zoom-out', 'fit', 'toggle-pan'],
  computed: {
    zoomPercent() {
      return Math.round(this.zoom * 100);
    }
  }
};
</script>

<style scoped>
.zoom-button:disabled { opacity: 0.4; cursor: not-allowed; }
.canvas-zoom-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  width: 100%;
  min-width: 0;
  padding: 3px 10px 6px;
  color: #506378;
  font-size: 12px;
}

.zoom-label {
  margin-right: 2px;
  color: #687b8e;
  font-weight: 600;
}

.zoom-stepper {
  display: flex;
  align-items: center;
  height: 32px;
  overflow: hidden;
  border: 1px solid #d8e2ec;
  border-radius: 7px;
  background: #f5f8fb;
}

.zoom-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 32px;
  height: 32px;
  padding: 0 7px;
  border: 1px solid #d8e2ec;
  border-radius: 7px;
  background: #f5f8fb;
  color: #506378;
  font: inherit;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}

.zoom-button:hover {
  border-color: #afc3d6;
  background: #eaf1f7;
}

.zoom-button:focus-visible {
  outline: 2px solid #6286a8;
  outline-offset: 2px;
}

.zoom-button svg {
  width: 17px;
  height: 17px;
  flex: 0 0 auto;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.zoom-stepper .zoom-button {
  border: 0;
  border-radius: 0;
  background: transparent;
}

.zoom-stepper .zoom-button:hover {
  background: #eaf1f7;
}

.zoom-level {
  min-width: 44px;
  color: #445a70;
  text-align: center;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.fit-button {
  white-space: nowrap;
}

.pan-button.is-active {
  border-color: #91aec8;
  background: #e4eef7;
  color: #355f82;
}

@media (max-width: 768px) {
  .canvas-zoom-controls {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 1fr) minmax(44px, 1fr);
    gap: 8px;
    padding: 4px 0 8px;
    box-sizing: border-box;
  }

  .zoom-label, .pan-label { display: none; }

  .zoom-stepper {
    width: 100%;
    height: 44px;
  }

  .zoom-stepper .zoom-button, .zoom-level { flex: 1; }

  .zoom-button {
    width: 100%;
    min-width: 44px;
    height: 44px;
  }
}

@media (max-width: 420px) {
  .canvas-zoom-controls {
    gap: 6px;
  }

  .zoom-label {
    display: none;
  }

  .pan-label {
    display: none;
  }

  .zoom-button {
    min-width: 44px;
    padding-right: 6px;
    padding-left: 6px;
  }
}
</style>